import React, { useEffect, useState } from 'react';
import clinicService from '../../../../../services/clinic.service';
import { useParams } from 'react-router-dom';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import ClinicServicesForm from './ClinicServicesForm';
import Loading from '../../../../common/Loading';
import {
    countEnabledServices,
    getServiceEntries,
    getServiceIconClass,
    getServiceIconTone,
    getServiceLabel,
} from './clinicServicesUtils';
const ActionTooltipButton = ({ tooltip, tooltipId, children, ...buttonProps }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip id={tooltipId}>{tooltip}</Tooltip>}>
        <Button {...buttonProps}>{children}</Button>
    </OverlayTrigger>
);

const ServiceRow = ({ serviceKey, enabled }) => (
    <div className="clinic-services-row">
        <span className="clinic-services-row__label">
            <span className={`clinic-services-row__icon clinic-services-row__icon--${getServiceIconTone(serviceKey)}`}>
                <i className={`bi ${getServiceIconClass(serviceKey)}`}></i>
            </span>
            {getServiceLabel(serviceKey)}
        </span>
        <span className={`clinic-services-status-badge ${enabled ? 'clinic-services-status-badge--enabled' : 'clinic-services-status-badge--disabled'}`}>
            <i className={`bi ${enabled ? 'bi-check-circle' : 'bi-x-circle'}`}></i>
            {enabled ? 'Enabled' : 'Disabled'}
        </span>
    </div>
);

function ClinicServicesCard() {
    const [clinicSettings, setClinicSettings] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { clinicId } = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [copied, setCopied] = useState(false);
    const [lastModified, setLastModified] = useState(null);

    useEffect(() => {
        loadSettings();
    }, [clinicId]);

    const loadSettings = () => {
        setIsLoading(true);
        setIsError(false);
        clinicService
            .getSettings(clinicId)
            .then((res) => {
                setClinicSettings(res.data);
                setLastModified(res.data.modifiedAt);
            })
            .catch((err) => {
                setIsError(true);
                setErrorMessage(err.response?.data?.message || 'Failed to load clinic services.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const updateSettings = (settings) => {
        setClinicSettings(settings);
        setLastModified(settings.modifiedAt);
        setIsEditing(false);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(clinicId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const counts = countEnabledServices(clinicSettings);
    const serviceGroups = getServiceEntries(clinicSettings);

    return (
        <div className="col-lg-8 clinic-services-panel">
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3 flex-wrap">
                <div>
                    <h5 className="clinic-services__title mb-1">
                        <i className="bi bi-gear-fill text-primary me-1"></i>
                        Clinic Services
                    </h5>
                    <p className="text-muted small mb-0">Enable or disable features and communication channels for this clinic.</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-light text-dark border font-monospace small">ID: {clinicId}</span>
                    <ActionTooltipButton
                        tooltip={copied ? 'Copied!' : 'Copy clinic ID'}
                        tooltipId="copy-clinic-services-id"
                        variant="outline-secondary"
                        size="sm"
                        onClick={copyToClipboard}
                    >
                        <i className={`bi ${copied ? 'bi-check2 text-success' : 'bi-copy'}`}></i>
                    </ActionTooltipButton>
                </div>
            </div>

            <div className="notifications-panel">
                <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-sliders"></i>
                        Feature Toggles
                    </span>
                    {!isLoading && !isEditing && (
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setIsEditing(true)}>
                            <i className="bi bi-pencil me-1"></i>
                            Edit
                        </button>
                    )}
                    {isEditing && (
                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setIsEditing(false)}>
                            <i className="bi bi-x-lg me-1"></i>
                            Cancel
                        </button>
                    )}
                </div>

                <div className="notifications-panel__body">
                    {isError && !isLoading && (
                        <div className="alert alert-warning border-0 py-2 px-3 small mb-3">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {errorMessage}
                        </div>
                    )}

                    {isLoading ? (
                        <Loading />
                    ) : isEditing ? (
                        <ClinicServicesForm clinicSettings={clinicSettings} updateSettings={updateSettings} onCancel={() => setIsEditing(false)} />
                    ) : (
                        <>
                            <div className="clinic-services-toolbar">
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="clinic-services-stat clinic-services-stat--total">{counts.total} services</span>
                                    <span className="clinic-services-stat clinic-services-stat--enabled">{counts.enabled} enabled</span>
                                    <span className="clinic-services-stat clinic-services-stat--disabled">{counts.disabled} disabled</span>
                                </div>
                                {lastModified && (
                                    <span className="clinic-services-meta">
                                        Last modified: {lastModified}
                                    </span>
                                )}
                            </div>

                            {serviceGroups.map((group, index) => (
                                <div key={group.title}>
                                    <div className={`clinic-services-section-title${index === 0 ? ' clinic-services-section-title--first' : ''}`}>
                                        {group.title}
                                    </div>
                                    {group.items.map((item) => (
                                        <ServiceRow key={item.key} serviceKey={item.key} enabled={item.value === true} />
                                    ))}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ClinicServicesCard;
